import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { apiService } from '../services/api';
import InlineFeedback from './InlineFeedback';
import { useFeedback } from '../hooks/useFeedback';
import { DashboardData } from '../features/dashboard/types';
import { DashboardView } from '../features/dashboard/DashboardView';
import { EyeIcon, EyeSlashIcon, LockClosedIcon } from '@heroicons/react/24/outline';

const SharedDashboardAccess: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const feedback = useFeedback();
  const { showError, clear } = feedback;

  useEffect(() => {
    if (!token) {
      showError('This share link is invalid.');
    }
  }, [token, showError]);

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPassword(value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!token || !password.trim()) {
      showError('Enter a password.');
      return;
    }

    setIsLoading(true);
    clear();

    try {
      const data = await apiService.getSharedDashboardData(token, password.trim());
      setDashboardData(data);
    } catch (err: any) {
      const message = err.response?.data?.message || 'Failed to open the dashboard. Try again.';
      showError(message);
    } finally {
      setIsLoading(false);
    }
  };

  // If dashboard data is loaded, show the DashboardView
  if (dashboardData) {
    return <DashboardView sharedData={dashboardData} />;
  }

  return (
    <div className="flex items-center justify-center min-h-[60vh] px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        <div className="bg-widget-bg rounded-lg border border-secondary p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="mx-auto w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mb-4">
              <LockClosedIcon className="h-8 w-8 text-primary" />
            </div>
            <h1 className="text-2xl font-bold text-heading mb-2">
              Access Shared Dashboard
            </h1>
            <p className="text-secondary text-sm">
              Enter the password to view this dashboard.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="password" className="block text-sm text-secondary mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={handlePasswordChange}
                  placeholder="Enter password"
                  className="w-full pl-3 pr-10 py-3 bg-body-bg border border-secondary rounded-md text-body-color placeholder-secondary/50 focus:outline-none focus:border-primary transition-colors"
                  disabled={isLoading}
                  autoFocus
                  autoComplete="off"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-secondary hover:text-body-color transition-colors"
                  disabled={isLoading}
                  title={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <EyeIcon className="w-5 h-5" />
                  ) : (
                    <EyeSlashIcon className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            <InlineFeedback feedback={feedback.feedback} />

            <button
              type="submit"
              disabled={isLoading || !password.trim()}
              className="w-full btn-primary-full py-3 flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <div className="h-5 w-5 rounded-full border-2 border-white/30 border-t-white animate-spin"></div>
                  <span>Verifying...</span>
                </>
              ) : (
                'View Dashboard'
              )}
            </button>
          </form>

          {/* Help Text */}
          <div className="mt-8 pt-6 border-t border-secondary">
            <p className="text-xs text-secondary text-center">
              Ask the dashboard owner if you do not have the password.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SharedDashboardAccess;